import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import { useHistory, useParams } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Typography from "@material-ui/core/Typography";
import HelpIcon from "@material-ui/icons/Help";
import IconButton from "@material-ui/core/IconButton";

import HoverTooltip from "@Components/Tooltip/HoverTooltip";
import FormikControl from "@Components/FormikControl/FormikControl";
import CustomSelect from "@Components/CustomSelect";
import eightAxios from "configs/axiosConfig";
import { useInterval } from "utils";
import {
  GRIEVANCE_LANGUAGE_CHOICES,
  TRANSLATION_DISCLAIMER_MESSAGE,
} from "configs/constants";
import { useTranslation } from "react-i18next";

const textProps = {
  style: {
    color: "red",
  },
};

const SOSTranslation = (props) => {
  const { passedValue } = props;
  const { t } = useTranslation();

  const [translatedText, setTranslatedText] = useState(null);
  const [loading, setLoading] = useState(false);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [translationCount, setTranslationCount] = useState(0);
  const [uniqueId, setUniqueId] = useState("");
  const [translationError, setTranslationError] = useState({});
  const params = useParams();
  const history = useHistory();

  useEffect(() => {
    if (passedValue) {
      setTranslationCount(passedValue);
    }
  }, [passedValue]);

  useEffect(() => {
    eightAxios
      .get(GRIEVANCE_LANGUAGE_CHOICES)
      .then((response) => {
        response.data.forEach(function (option) {
          option.name = option.name.toUpperCase();
        });
        setLanguageOptions(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  const handleSubmitTranslation = () => {
    setLoading(true);
    const payload = {
      grievance_pk: params.id,
      from_language: selectedLanguage,
    };
    eightAxios
      .post(`sos/${params.id}/translate/`, payload)
      .then((response) => {
        setUniqueId(response.data.unique_code);
        setTranslatedText(response.data.status);

        const decreaseTranslationCountPayload = {
          allowed_translation_count: translationCount - 1,
        };
        eightAxios
          .post(
            `sos/${params.id}/translation-count/`,
            decreaseTranslationCountPayload
          )
          .then((response) => {
            setTranslationCount(response.data.allowed_translation_count);
          })
          .catch((error) => {
            console.error(error);
          });
      })
      .catch((error) => {
        console.error(error);
        setTranslatedText("Error translating grievance. Please try again.");
        setLoading(false);
      });
  };

  const handleTranslationConfirmation = (event) => {
    event.preventDefault();
    const payload = {
      text_message: translatedText,
      is_translated: true,
      translated_from: selectedLanguage,
      translated_text: translatedText,
    };
    eightAxios
      .post(`sos/${params.id}/save-translation/`, payload)
      .then((response) => {
        if (response.status === 201) {
          history.go(0);
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useInterval(
    async () => {
      const resp = await eightAxios
        .get(`sos/${params.id}/translation-result/?unique_id=${uniqueId}`)
        .catch((error) => {
          if (error && error.response && error.response.status === 400) {
            setTranslatedText(error.response.data.message);
            setTranslationError(textProps);
            setLoading(false);
          } else {
            setTranslatedText("There is something wrong with network");
            setTranslationError(textProps);
            setLoading(false);
          }
        });
      if (resp && resp.status === 200) {
        setTranslatedText(resp.data.result);
        setTranslationError({});
        setLoading(false);
      }
    },
    loading && uniqueId ? 2000 : null
  );

  return (
    <Grid container spacing={2}>
      <Grid item container spacing={1} xs={12} alignItems="center">
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <CustomSelect
            label={t("Translate From")}
            options={languageOptions}
            value={selectedLanguage}
            onChange={(event) => setSelectedLanguage(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={6} lg={4}>
          <FormikControl
            control="input"
            label="Translate To"
            defaultValue="ENGLISH"
            name="translate_to"
            disabled
          />
        </Grid>
        <Grid item container spacing={2} xs={12} lg={4}>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmitTranslation}
              disabled={loading || !selectedLanguage || translationCount <= 0}
              style={{
                borderRadius: 0,
                top: "2px",
                textTransform: "capitalize",
              }}
            >
              {t("Translate")}
            </Button>
          </Grid>
          <Grid item style={{ position: "relative" }}>
            <Typography>
              {t("Allow change")} {translationCount} {t("times")} {" "}
              <HoverTooltip
                title={ `${t("You have")} ${translationCount} ${t("translation left.")}`}
              >
                <IconButton>
                  <HelpIcon style={{ position: "absolute" }} />
                </IconButton>
              </HoverTooltip>
            </Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid item container xs={12}>
        <Grid item xs={12}>
          <FormikControl
            multiline
            rows={4}
            control="input"
            label="Translated Text"
            error={translationError}
            defaultValue={translatedText}
            name="translate_to"
            inputProps={translationError}
            disabled
          />
          <Typography
            variant="subtitle2"
            style={{
              fontSize: 10,
              color: "#848282",
              fontStyle: "italic",
            }}
          >
            Disclaimer: {TRANSLATION_DISCLAIMER_MESSAGE}
          </Typography>
        </Grid>
        {loading ? (
          <Grid item xs={12}>
            <LinearProgress />
          </Grid>
        ) : null}
        <Grid item container xs={12} justify="flex-end">
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              style={{
                borderRadius: 0,
                top: "2px",
                textTransform: "capitalize",
              }}
              disabled={loading || !translatedText}
              onClick={handleTranslationConfirmation}
            >
              {t("Save")}
            </Button>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

SOSTranslation.propTypes = {
  passedValue: PropTypes.number,
};

export default SOSTranslation;
